// InnerEaze quick add: the + button on product cards (touch devices).
// Opens a small sheet with the product's options, adds to cart, then tells Atelier's
// cart drawer and cart count to refresh (same CartLinesUpdateEvent Atelier's own forms send).
import { CartLinesUpdateEvent } from '@shopify/events';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const money = (cents) => {
  const fmt = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
  try {
    const d = cents % 100 ? 2 : 0;
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: fmt, minimumFractionDigits: d, maximumFractionDigits: d }).format(cents / 100);
  } catch (e) {
    return '₹' + (cents / 100).toFixed(cents % 100 ? 2 : 0);
  }
};
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

let dlg;
let state;

function sheet() {
  if (dlg) return dlg;
  dlg = document.createElement('dialog');
  dlg.className = 'ie ie-qa';
  dlg.setAttribute('aria-label', 'Quick add');
  document.body.appendChild(dlg);
  dlg.addEventListener('click', (e) => {
    if (e.target === dlg || e.target.closest('[data-qa-close]')) close();
    const pill = e.target.closest('[data-opt]');
    if (pill) {
      state.sel[Number(pill.dataset.opt)] = pill.dataset.val;
      render();
    }
    if (e.target.closest('[data-qa-add]')) add();
  });
  dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
  return dlg;
}

function variant() {
  return state.p.variants.find((v) => v.options.every((o, i) => o === state.sel[i]));
}

function render() {
  // keep keyboard focus on the same pill after the sheet re-renders
  const f = dlg.contains(document.activeElement) && document.activeElement.dataset.opt !== undefined
    ? [document.activeElement.dataset.opt, document.activeElement.dataset.val] : null;
  const p = state.p;
  const v = variant();
  const img = (v && v.featured_image && v.featured_image.src) || p.featured_image;
  const imgSrc = img ? img + (img.includes('?') ? '&' : '?') + 'width=160' : ''; // Shopify CDN resizes by query
  const groups = p.options.map((opt, i) => {
    const name = typeof opt === 'string' ? opt : opt.name;
    const values = typeof opt === 'string' ? [...new Set(p.variants.map((x) => x.options[i]))] : opt.values;
    const pills = values.map((val) => {
      const on = state.sel[i] === val;
      const hex = /colou?r/i.test(name) && state.swatches && state.swatches[val];
      return `<button type="button" class="ie-qa__pill${on ? ' is-on' : ''}" data-opt="${i}" data-val="${esc(val)}" aria-pressed="${on}">${hex ? `<i style="background:${esc(hex)}"></i>` : ''}${esc(val)}</button>`;
    }).join('');
    return `<fieldset class="ie-qa__grp"><legend>${esc(name)}: <b>${esc(state.sel[i])}</b></legend><div class="ie-qa__pills">${pills}</div></fieldset>`;
  }).join('');
  const ok = v && v.available;
  dlg.innerHTML = `
    <div class="ie-qa__in">
      <div class="ie-qa__head">
        ${imgSrc ? `<img src="${esc(imgSrc)}" alt="" width="64" height="80">` : ''}
        <div><span class="ie-qa__vendor">${esc(p.vendor)}</span><b class="ie-qa__title">${esc(p.title)}</b><span class="ie-qa__price">${money(v ? v.price : p.price)}</span></div>
        <button type="button" class="ie-qa__x" data-qa-close aria-label="Close"><svg class="ie-i" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button>
      </div>
      ${p.variants.length > 1 ? groups : ''}
      <p class="ie-qa__err" role="alert" hidden></p>
      <button type="button" class="ie-btn ie-qa__add" data-qa-add ${ok ? '' : 'disabled'}>${ok ? 'Add to Cart' : 'Sold Out'}</button>
      <a class="ie-qa__more" href="${esc(p.url || root + 'products/' + p.handle)}">View Full Details</a>
    </div>`;
  if (f) [...dlg.querySelectorAll('[data-opt]')].find((b) => b.dataset.opt === f[0] && b.dataset.val === f[1])?.focus();
}

function close() {
  if (!dlg || !dlg.open) return;
  dlg.close();
  document.documentElement.removeAttribute('scroll-lock');
  if (state && state.opener) state.opener.focus({ preventScroll: true });
}

async function open(btn) {
  const handle = btn.dataset.ieQa;
  btn.setAttribute('aria-busy', 'true');
  try {
    const res = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('product');
    const p = await res.json();
    let swatches = null;
    try { swatches = JSON.parse(btn.dataset.swatches || 'null'); } catch (e) { swatches = null; }
    const first = p.variants.find((v) => v.available) || p.variants[0];
    state = { p, sel: [...first.options], swatches, opener: btn };
    sheet();
    render();
    dlg.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    // single-variant products: nothing to choose, add straight away
    if (p.variants.length === 1) add();
  } catch (e) {
    window.location.href = root + 'products/' + handle;
  } finally {
    btn.removeAttribute('aria-busy');
  }
}

async function add() {
  const v = variant();
  if (!v || !v.available) return;
  const btn = dlg.querySelector('[data-qa-add]');
  const err = dlg.querySelector('.ie-qa__err');
  btn.disabled = true;
  btn.textContent = 'Adding...';
  const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
  const body = new FormData();
  body.append('id', v.id);
  body.append('quantity', '1');
  if (sectionIds.length) body.append('sections', sectionIds.join(','));
  try {
    const res = await fetch(root + 'cart/add.js', { method: 'POST', body, headers: { Accept: 'application/json' } });
    const json = await res.json();
    if (json.status) throw new Error(json.description || json.message || 'Could not add to cart');
    close();
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(v.id), quantity: 1 }],
      promise: deferred.promise,
    }));
    const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'ie-quick-add', itemCount: 1, productId: String(state.p.id), sections: json.sections, didError: false },
    });
    const drawer = document.getElementById('cart-drawer');
    setTimeout(() => { if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open(); }, 150);
  } catch (e) {
    btn.disabled = false;
    btn.textContent = 'Add to Cart';
    err.textContent = e.message || 'Something went wrong. Please try again.';
    err.hidden = false;
  }
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-ie-qa]');
  if (!btn) return;
  e.preventDefault();
  open(btn);
});
